// ─── Unified Card Search ──────────────────────────────────────────
// Combines Pokémon TCG API + OPTCG API into a single search interface

import { searchPokemonCards, getPokemonCardById, getPokemonSets, getPokemonCardsBySet, setPokemonLang, POKEMON_LANGUAGES } from "./pokemonApi";
import { searchOnePieceCards, getOnePieceCardById, getOnePieceSets, getOnePieceCardsBySet } from "./onePieceApi";

/**
 * Search cards across all games
 * @param {string} query - search term
 * @param {string} gameFilter - "all" | "pokemon" | "one_piece"
 * @param {number} page
 * @param {number} pageSize
 */
export async function searchCards(query, gameFilter = "all", page = 1, pageSize = 24) {
  if (!query || query.trim().length < 2) {
    return { cards: [], totalCount: 0, page, pageSize };
  }

  const promises = [];

  if (gameFilter === "all" || gameFilter === "pokemon") {
    promises.push(
      searchPokemonCards(query, page, gameFilter === "all" ? 12 : pageSize)
        .then((res) => ({ game: "pokemon", ...res }))
        .catch(() => ({ game: "pokemon", cards: [], totalCount: 0 }))
    );
  }

  if (gameFilter === "all" || gameFilter === "one_piece") {
    promises.push(
      searchOnePieceCards(query, page, gameFilter === "all" ? 12 : pageSize)
        .then((res) => ({ game: "one_piece", ...res }))
        .catch(() => ({ game: "one_piece", cards: [], totalCount: 0 }))
    );
  }

  const responses = await Promise.all(promises);
  let allCards = [];
  let totalCount = 0;
  responses.forEach((res) => {
    allCards = allCards.concat(res.cards);
    totalCount += res.totalCount;
  });

  return { cards: allCards, totalCount, page, pageSize };
}

/**
 * Get card by ID (checks prefix to determine source)
 */
export async function getCardById(cardId) {
  if (cardId.startsWith("pkmn-")) return getPokemonCardById(cardId.replace("pkmn-", ""));
  if (cardId.startsWith("op-")) return getOnePieceCardById(cardId);
  return null;
}

export { getPokemonSets, getPokemonCardsBySet, getOnePieceSets, getOnePieceCardsBySet, setPokemonLang, POKEMON_LANGUAGES };

/**
 * Refresh card data from API to get latest prices
 * ONLY updates price fields — preserves identity (name, image, variant)
 */
export async function refreshCardData(card) {
  if (!card) return null;
  try {
    let fresh = null;
    if (card.game === "pokemon" && card.api_id) {
      fresh = await getPokemonCardById(card.api_id);
    } else if (card.game === "one_piece" && card.api_id) {
      fresh = await getOnePieceCardById(card.id);
    }

    if (!fresh) return null;

    // Only take price fields from fresh data — keep everything else from saved card
    return {
      ...card,
      // Pokémon prices
      tcgplayer_prices: fresh.tcgplayer_prices || card.tcgplayer_prices,
      cardmarket_prices: fresh.cardmarket_prices || card.cardmarket_prices,
      // One Piece prices
      market_price: fresh.market_price || card.market_price,
      inventory_price: fresh.inventory_price || card.inventory_price,
      date_scraped: fresh.date_scraped || card.date_scraped,
    };
  } catch (err) {
    console.error("Card refresh error:", err);
  }
  return null;
}
